import React from 'react'
import PropTypes from 'prop-types'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'
import useWindowSize from '@shared/hooks/windowSize'
import Partnership from './Partnership'
import WagonWheel from './wagonWheel'
import SpiderStats from './spiderStats'
import NoData from '../noData'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend)

function StatGraphs({ graphData, matchDetail, inningData, activeTab, teamNames, isTestMatch, is100 }) {
  const obj = {}
  graphData.forEach(item => {
    obj[item.nInningNumber] = item
  })
  const [width] = useWindowSize()

  const inningsData = Object.values(obj)

  const colors = ['#F25824', '#252775', '#252775', '#F25824']

  const labels = []
  const temp = []
  const [datasets, setDatasets] = React.useState(temp)

  inningsData.forEach((inning, index) => {
    labels.push(inning.sName)
    temp.push({
      label: inning.sName,
      data: activeTab === 'aManhattan' ? inning[activeTab]?.map((runType) => {
        return { run: parseInt(runType.nRuns), wicket: parseInt(runType?.nWickets), inning: inning?.nInningNumber }
      }) : inning[activeTab]?.map(runType =>
        parseInt(
          activeTab === 'aWorm' ? runType.nRuns : activeTab === 'aRunRates' ? runType.nRunRate : runType.sValue
        )
      ),
      backgroundColor: colors[index],
      borderColor: colors[index],
      borderWidth: activeTab === 'aWorm' ? 4 : 1,
      categoryPercentage: 0.9,
      barPercentage: 1,
      borderRadius: 1
    })
  })

  function getNewInningData() {
    const d = []

    inningsData.forEach((inning, index) => {
      labels.push(inning.sName)
      d?.push({
        label: inning.sName,
        data:
          activeTab === 'aManhattan' ? inning[activeTab]?.map((runType) => ({
            run: parseInt(runType.nRuns),
            wicket: parseInt(runType?.nWickets),
            inning: inning?.nInningNumber
          })) : inning[activeTab]?.map((runType) =>
            parseInt(activeTab === 'aWorm' ? runType.nRuns : activeTab === 'aRunRates' ? runType.nRunRate : runType.sValue)
          ),
        backgroundColor: colors[index],
        borderColor: colors[index],
        borderWidth: activeTab === 'aWorm' ? 4 : 1,
        categoryPercentage: 0.9,
        barPercentage: 1,
        borderRadius: 1
      })
    })

    setDatasets(d)
  }

  React.useEffect(() => {
    getNewInningData()
  }, [activeTab])

  const uniqueRunTypes = new Set()
  inningsData.forEach(inning => {
    inning[activeTab]?.forEach(runType => {
      uniqueRunTypes.add(
        activeTab === 'aManhattan' || activeTab === 'aWorm' ? runType.nOver : activeTab === 'aRunRates' ? runType.nOver : runType.sName
      )
    })
  })

  const data = {
    labels: Array.from(uniqueRunTypes),
    datasets: datasets
  }

  const horizontalOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          borderRadius: 4,
          font: {
            size: width < 767 ? 10 : 16
          },
          color: 'gray',
          boxWidth: 16
        }
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'y',
    scales: {
      x: {
        title: {
          display: true,
          // text: 'Overs',
          align: 'end'
        },
        grid: {
          display: false
        },
        border: {
          color: 'gray'
        }
      },
      y: {
        title: {
          display: true,
          // text: 'Runs',
          align: 'end'
        },
        grid: {
          color: 'gray'
        },
        ticks: {
          stepSize: 6,
          beginAtZero: true
        },
        border: {
          color: 'gray'
        }
      }
    }
  }

  const verticalOptions = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'x',
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          borderRadius: 4,
          font: {
            size: width < 767 ? 10 : 16
          },
          color: 'gray',
          boxWidth: 16
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetIndex = context.datasetIndex
            const index = context.dataIndex
            const innings = data?.datasets[datasetIndex]
            const item = innings.data[index]
            if (activeTab === 'aManhattan') {
              return [`${innings.label}`, `Runs: ${item.run}`, `Wickets: ${item.wicket}`]
            } else {
              return [`${innings.label}`]
            }
          }
        }
      },
      datalabels: {
        color: '#DFE1E2',
        font: {
          weight: 400,
          size: 10
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Overs',
          align: 'end'
        },
        grid: {
          display: false,
          color: (ctx) => (ctx.index !== 0 ? 'transparent' : '#272D2F')
        },
        border: {
          color: 'gray'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Runs',
          align: 'end'
        },
        grid: {
          color: 'gray'
        },
        ticks: {
          stepSize: 6,
          beginAtZero: true
        },
        border: {
          color: 'gray'
        }
      }
    }
  }
  const chartData = {
    labels: data.labels,
    datasets: data?.datasets?.map((dataset) => ({
      label: dataset.label,
      data: dataset?.data?.map((item) => item.run),
      backgroundColor: dataset.backgroundColor,
      borderColor: dataset.borderColor,
      borderWidth: dataset.borderWidth,
      categoryPercentage: dataset.categoryPercentage,
      barPercentage: dataset.barPercentage,
      parsing: {
        yAxisKey: 'run'
      }
    }))
  }
  const manhattanOptions = {
    ...verticalOptions,
    plugins: {
      ...verticalOptions.plugins,
      datalabels: {
        align: 'end',
        anchor: 'end',
        offset: 4,
        formatter: function(value, context) {
          // Get the dataset and index
          const datasetIndex = context.datasetIndex
          const index = context.dataIndex

          // Get the wicket count (assuming data structure has wicket property)
          const dataPoint = data.datasets[datasetIndex].data[index]
          const wickets = dataPoint.wicket
          // Return wicket symbols if there are any wickets, otherwise return empty string
          return wickets > 0 ? '🎾' : ''
        },
        color: function() {
          return '#CA687F'
        },
        font: {
          weight: 'bold',
          size: 12
        }
      }
    }
  }
  const renderGraph = () => {
    switch (activeTab) {
      case 'aManhattan':
        return <Bar options={manhattanOptions} data={chartData} />
      case 'aWorm':
        return <Line options={verticalOptions} data={data} />
      case 'aRunTypes':
        return <Bar options={horizontalOptions} data={data} />
      case 'aWickets':
        return <Bar options={horizontalOptions} data={data} />
      case 'aExtras':
        return <Bar options={horizontalOptions} data={data} />
      case 'aRunRates':
        return <Line options={verticalOptions} data={data} />
      case 'aPartnership':
        return <Partnership graphData={graphData} is100={is100} />
      case 'spider':
        return (matchDetail ? <SpiderStats matchDetail={matchDetail} inningData={inningData} /> : <NoData title='The numbers are warming up in the nets Stay tuned for all the big hits and stats soon!' />)
      case 'wagonWheel':
        return (matchDetail ? <WagonWheel matchDetail={matchDetail} inningData={inningData} /> : <NoData title='Hold tight! The wagon wheel is gearing up for action – runs will start flowing soon! ' />)
      default:
        return null
    }
  }

  return renderGraph()
}

StatGraphs.propTypes = {
  graphData: PropTypes.array,
  activeTab: PropTypes.string,
  teamNames: PropTypes.string,
  isTestMatch: PropTypes.bool,
  is100: PropTypes.bool,
  inningData: PropTypes.object,
  matchDetail: PropTypes.object
}

export default StatGraphs
